import { HIGHLIGHT_COLORS } from './annotate';
import type { SavedHighlight } from './PassageHighlighter';

/** Highlights of the open section, keyed by `${passageId}:${pageIndex}`. */
export type HighlightsByPassage = Record<string, SavedHighlight[]>;

interface StoredHighlights {
  sectionId: string;
  byPassage: HighlightsByPassage;
}

/*
 * Reading highlights are kept in this browser so a reload does not wipe them.
 * One entry per attempt, holding the open section only: moving on to another
 * section drops them, as in the computer-delivered test. Storage can be
 * blocked (private mode, full quota); highlights then still work, they just
 * do not survive a reload.
 */
const storageKey = (attemptId: string) => `examing.exam-highlights.${attemptId}`;

const isSavedHighlight = (value: unknown): value is SavedHighlight => {
  if (!value || typeof value !== 'object') return false;
  const { id, start, end, color, note } = value as Record<string, unknown>;
  return (
    typeof id === 'string' &&
    typeof start === 'number' &&
    typeof end === 'number' &&
    Number.isInteger(start) &&
    Number.isInteger(end) &&
    start >= 0 &&
    end > start &&
    typeof note === 'string' &&
    (HIGHLIGHT_COLORS as readonly unknown[]).includes(color)
  );
};

export function loadHighlights(attemptId: string, sectionId: string): HighlightsByPassage {
  try {
    const raw = localStorage.getItem(storageKey(attemptId));
    if (!raw) return {};
    const stored = JSON.parse(raw) as Partial<StoredHighlights> | null;
    if (stored?.sectionId !== sectionId || !stored.byPassage || typeof stored.byPassage !== 'object') return {};
    const byPassage: HighlightsByPassage = {};
    for (const [key, list] of Object.entries(stored.byPassage)) {
      const valid = Array.isArray(list) ? list.filter(isSavedHighlight) : [];
      if (valid.length) byPassage[key] = valid;
    }
    return byPassage;
  } catch {
    return {};
  }
}

export function saveHighlights(attemptId: string, sectionId: string, byPassage: HighlightsByPassage): void {
  try {
    if (Object.values(byPassage).some((list) => list.length)) {
      const stored: StoredHighlights = { sectionId, byPassage };
      localStorage.setItem(storageKey(attemptId), JSON.stringify(stored));
    } else {
      localStorage.removeItem(storageKey(attemptId));
    }
  } catch {
    // Storage blocked: see above.
  }
}

export function clearHighlights(attemptId: string): void {
  try {
    localStorage.removeItem(storageKey(attemptId));
  } catch {
    // Storage blocked: see above.
  }
}
